import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { List } from 'react-window';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Inbox, Loader2, Search, RefreshCw, Send, Sparkles, MessageSquareReply,
  Paperclip, ExternalLink, CalendarPlus, ListChecks, CheckSquare, Square, Keyboard,
  ChevronRight, ArrowLeft, Mail, Flame, ReplyAll, Forward, Save, Trash2, ClipboardList,
  Zap, Star,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RecipientInput from '../components/correo/RecipientInput';
import ComposerAiToolbar from '../components/correo/ComposerAiToolbar';
import AttachmentsInput from '../components/correo/AttachmentsInput';
import EmailViewer from '../components/EmailViewer';
import { useAuth } from '../context/AuthContext';
import { req } from '../services/api';

// Iter68 — clave de localStorage para borrador autosalvado
const DRAFT_KEY = 'inside-crm:correo-borrador';

// Saneamiento básico de HTML (quita scripts/styles, on*, javascript:)
// ctx: { cuentaId, messageId } — si se pasa, resuelve cid: via endpoint proxy
function sanitizeHtml(html, ctx = {}) {
  if (!html) return '';
  let s = String(html);
  s = s.replace(/<script[\s\S]*?<\/script>/gi, '');
  s = s.replace(/<style[\s\S]*?<\/style>/gi, '');
  s = s.replace(/\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  s = s.replace(/(href|src)\s*=\s*("|')javascript:[^"']*\2/gi, '$1="#"');
  // Eleva http:// → https:// en src/href/background para evitar mixed content blocking
  s = s.replace(/(src|href|background)\s*=\s*("|')http:\/\//gi, '$1=$2https://');
  // Resuelve cid: (logos en firmas) via endpoint proxy autenticado
  if (ctx.cuentaId && ctx.messageId) {
    const token = localStorage.getItem('token') || '';
    const base = process.env.REACT_APP_BACKEND_URL;
    s = s.replace(
      /src\s*=\s*(["'])cid:([^"']+)\1/gi,
      (_, q, cid) => `src="${base}/api/correo/cuentas/${ctx.cuentaId}/mensajes/${encodeURIComponent(ctx.messageId)}/inline/${encodeURIComponent(cid)}?t=${token}"`,
    );
  }
  return s;
}


// Normaliza subject para fallback threading (quita Re:/Fwd:/RE: case-insensitive, trimea)
const normalizeSubject = (s) => (s || '')
  .replace(/^(re|fwd|rv|r)\s*:\s*/gi, '')
  .replace(/^(re|fwd|rv|r)\s*:\s*/gi, '')
  .trim()
  .toLowerCase();

// Agrupa correos por thread_id | in_reply_to | subject normalizado (fallback RFC)
function groupByThread(items) {
  const threads = new Map();
  const orden = [];
  for (const m of items) {
    const key = m.thread_id || m.conversation_id || (
      m.subject ? `subj:${normalizeSubject(m.subject)}_${m.cuenta_id || ''}` : `id:${m.message_id}`
    );
    if (!threads.has(key)) {
      threads.set(key, { key, items: [], last: null });
      orden.push(key);
    }
    threads.get(key).items.push(m);
  }
  const out = [];
  for (const k of orden) {
    const t = threads.get(k);
    t.items.sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
    t.last = t.items[0];
    out.push(t);
  }
  out.sort((a, b) => new Date(b.last.received_at) - new Date(a.last.received_at));
  return out;
}

const normalizarFechaPropuestaIA = (d) => {
  if (!d) return '';
  const s = String(d).trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
};

const fmtRelDate = (s) => {
  if (!s) return '';
  try {
    const d = new Date(s);
    const now = new Date();
    const diffH = (now - d) / 3600000;
    if (diffH < 24 && now.toDateString() === d.toDateString()) {
      return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
    if (diffH < 168) return d.toLocaleDateString('es-ES', { weekday: 'short' });
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  } catch (_) { return s; }
};

// ════════════════════════════════════════════════════════════════════
// Row item (react-window list)
// ════════════════════════════════════════════════════════════════════
const ThreadRow = ({ index, style, thread, onClick, onDoubleClick, onToggleUrgente, isActive }) => {
  const m = thread.last;
  const nItems = thread.items.length;
  return (
    <div
      style={style}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`px-3 py-2 border-b border-ink-100 cursor-pointer transition flex flex-col gap-0.5 ${
        isActive ? 'bg-brand-50 border-l-2 border-l-brand-500'
        : m.urgente ? 'bg-red-50/40 hover:bg-red-50 border-l-2 border-l-red-400'
        : m.is_read === false ? 'bg-white hover:bg-ink-50 font-medium'
        : 'bg-ink-50/40 hover:bg-ink-50'
      }`}
      data-testid={`thread-row-${m.message_id}`}
    >
      <div className="flex items-center gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleUrgente && onToggleUrgente(m); }}
          className={`shrink-0 p-0.5 rounded transition ${m.urgente ? 'text-red-600 hover:text-red-700' : 'text-ink-300 hover:text-red-500'}`}
          data-testid={`btn-urgente-${m.message_id}`}
          title={m.urgente ? 'Quitar urgente' : 'Marcar urgente'}
        >
          <Flame className="w-3.5 h-3.5" />
        </button>
        <div className={`text-xs truncate flex-1 ${m.is_read === false ? 'text-ink-900 font-semibold' : 'text-ink-700'}`}>
          {m.from_name || m.from_address || '(sin remitente)'}
          {nItems > 1 && <span className="ml-1 text-ink-400">({nItems})</span>}
        </div>
        <div className="text-[10px] text-ink-400 shrink-0">{fmtRelDate(m.received_at)}</div>
      </div>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs truncate flex-1 ${m.is_read === false ? 'text-ink-900' : 'text-ink-600'}`}>
          {m.subject || '(sin asunto)'}
        </span>
        {m.has_attachments && <Paperclip className="w-3 h-3 text-ink-400 shrink-0" />}
        {m.es_oportunidad && <span className="text-[9px]" title="Oportunidad detectada">🎯</span>}
      </div>
      <div className="text-[11px] text-ink-500 truncate">{m.preview}</div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// Sprint 1 — Panel de vinculación manual de empresa a correo huérfano
// ════════════════════════════════════════════════════════════════════
const DOMINIOS_GENERICOS_SET = new Set([
  'gmail.com','outlook.com','hotmail.com','yahoo.com','yahoo.es',
  'icloud.com','live.com','msn.com','protonmail.com','proton.me','aol.com',
]);

function VincularEmpresaPanel({ msg, onVinculado, onIgnorado, modoInicial = 'buscar', entidadPreId = null, entidadPreNombre = '' }) {
  const dominio = msg?.from_address?.split('@')[1] || '';
  const dominioGenerico = !dominio || DOMINIOS_GENERICOS_SET.has(dominio);
  const sugerirContacto = !msg?.vinculado_contacto_id && !!msg?.from_address;
  const fromParts = (msg?.from_name || '').trim().split(/\s+/);

  // Estado principal
  const [modo, setModo] = useState(modoInicial); // 'buscar' | 'crear' | 'contacto'

  // Buscar empresa
  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionada, setSeleccionada] = useState(null); // { id, nombre }
  const [guardarDominio, setGuardarDominio] = useState(!dominioGenerico);
  const [vinculando, setVinculando] = useState(false);
  const timerRef = useRef(null);
  const cifTimerRef = useRef(null);

  // Crear empresa
  const [formCrear, setFormCrear] = useState({ nombre: '', cif: '', dominio: dominioGenerico ? '' : dominio });
  const [cifExistente, setCifExistente] = useState(null); // { id, nombre } o null
  const [buscandoCif, setBuscandoCif] = useState(false);
  const [creando, setCreando] = useState(false);
  const [nombreSimilares, setNombreSimilares] = useState([]);
  const [buscandoNombre, setBuscandoNombre] = useState(false);
  const nombreTimerRef = useRef(null);
  const [analizandoIA, setAnalizandoIA] = useState(false);
  const [iaMsg, setIaMsg] = useState('');

  // Contacto (etapa posterior a vincular empresa, o empresa pre-vinculada)
  const [entidadIdFinal, setEntidadIdFinal] = useState(entidadPreId);
  const [entidadNombreFinal, setEntidadNombreFinal] = useState(entidadPreNombre);
  const [formContacto, setFormContacto] = useState({
    nombre: fromParts[0] || '',
    apellidos: fromParts.slice(1).join(' '),
    cargo: '',
  });
  const [creandoContacto, setCreandoContacto] = useState(false);
  // Empresa editable en el formulario de contacto
  const [contactoEmpresaId, setContactoEmpresaId] = useState(null);
  const [contactoEmpresaNombre, setContactoEmpresaNombre] = useState('');
  const [contactoEmpresaQuery, setContactoEmpresaQuery] = useState('');
  const [contactoEmpresaResultados, setContactoEmpresaResultados] = useState([]);
  const [contactoEmpresaBuscando, setContactoEmpresaBuscando] = useState(false);
  const contactoEmpresaTimer = useRef(null);

  // Ignorar
  const [ignorando, setIgnorando] = useState(false);

  // ── Buscar empresa ──────────────────────────────────────────
  const buscar = (q) => {
    setQuery(q);
    setSeleccionada(null);
    clearTimeout(timerRef.current);
    if (!q.trim()) { setResultados([]); return; }
    timerRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(q.trim())}&limit=6`);
        setResultados(Array.isArray(d) ? d : (d.items || []));
      } catch (_) { setResultados([]); }
      finally { setBuscando(false); }
    }, 300);
  };

  // Vincula en backend y decide si pasa a modo contacto
  const _doVincular = async (entidadId, entidadNombre, doGuardarDominio) => {
    await req(`/api/correo/mensajes/${encodeURIComponent(msg.message_id)}/vincular`, {
      method: 'PATCH',
      body: JSON.stringify({ entidad_id: entidadId, guardar_dominio: doGuardarDominio }),
    });
    if (sugerirContacto) {
      setEntidadIdFinal(entidadId);
      setEntidadNombreFinal(entidadNombre);
      setContactoEmpresaId(entidadId);
      setContactoEmpresaNombre(entidadNombre);
      setModo('contacto');
    } else {
      onVinculado(msg.message_id, entidadId, entidadNombre);
    }
  };

  const confirmarVincular = async () => {
    if (!seleccionada) return;
    setVinculando(true);
    try { await _doVincular(seleccionada.id, seleccionada.nombre, guardarDominio); }
    catch (e) { toast.error(`Vincular: ${e.message}`); }
    finally { setVinculando(false); }
  };

  // ── Crear empresa ───────────────────────────────────────────
  const verificarCif = (cif) => {
    clearTimeout(cifTimerRef.current);
    setCifExistente(null);
    if (cif.trim().length < 5) return;
    cifTimerRef.current = setTimeout(async () => {
      setBuscandoCif(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(cif.trim())}&limit=3`);
        const its = Array.isArray(d) ? d : (d.items || []);
        const match = its.find((r) => (r.cif || '').toLowerCase() === cif.trim().toLowerCase());
        setCifExistente(match ? { id: match.id, nombre: match.nombre_comercial || match.razon_social } : null);
      } catch (_) { setCifExistente(null); }
      finally { setBuscandoCif(false); }
    }, 400);
  };

  const verificarNombre = (nombre) => {
    clearTimeout(nombreTimerRef.current);
    setNombreSimilares([]);
    if (nombre.trim().length < 3) return;
    nombreTimerRef.current = setTimeout(async () => {
      setBuscandoNombre(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(nombre.trim())}&limit=3`);
        setNombreSimilares(Array.isArray(d) ? d : (d.items || []));
      } catch (_) { setNombreSimilares([]); }
      finally { setBuscandoNombre(false); }
    }, 400);
  };

  const analizarConIA = async () => {
    if (!dominio) { toast.error('No se puede inferir la empresa de este correo'); return; }
    setAnalizandoIA(true); setIaMsg('');
    try {
      // Buscar en Perplexity usando el dominio del remitente como identificador
      const r = await req('/api/perplexity/buscar-empresa', {
        method: 'POST',
        body: JSON.stringify({ razon_social: dominio }),
      });
      if (!r.razon_social && !r.cif && !r.web) {
        setIaMsg('Sin datos encontrados para este dominio.');
        return;
      }
      // Comprobar si ya existe en el CRM
      const qTermino = r.cif || r.razon_social || dominio;
      const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(qTermino)}&limit=5`);
      const its = Array.isArray(d) ? d : (d.items || []);
      let existenteId = null; let existenteNombre = null;
      if (r.cif) {
        const match = its.find((x) => (x.cif || '').toUpperCase() === r.cif.toUpperCase());
        if (match) { existenteId = match.id; existenteNombre = match.nombre_comercial || match.razon_social; }
      }
      if (!existenteId && r.razon_social && its.length > 0) {
        existenteId = its[0].id; existenteNombre = its[0].nombre_comercial || its[0].razon_social;
      }
      if (existenteId) {
        // Ya existe → pre-seleccionar y mostrar confirmación
        setSeleccionada({ id: existenteId, nombre: existenteNombre });
        setIaMsg(`✓ Ya registrada en el CRM: ${existenteNombre}${r.cif ? ' · ' + r.cif : ''}`);
      } else {
        // No existe → pre-rellenar formulario y cambiar a modo crear
        let dominioWeb = dominio;
        if (r.web) {
          try { dominioWeb = new URL(r.web.startsWith('http') ? r.web : `https://${r.web}`).hostname.replace(/^www\./, ''); } catch (_) {}
        }
        const nombreFinal = r.razon_social || dominio;
        setFormCrear({ nombre: nombreFinal, cif: r.cif || '', dominio: dominioWeb });
        if (r.cif) verificarCif(r.cif);
        verificarNombre(nombreFinal);
        setModo('crear');
      }
    } catch (e) {
      toast.error(`IA: ${e.message}`);
    } finally { setAnalizandoIA(false); }
  };

  const crearEmpresa = async () => {
    if (!formCrear.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (cifExistente) { toast.error('Ese CIF ya existe — usa "Usar esta empresa".'); return; }
    setCreando(true);
    try {
      const body = {
        razon_social: formCrear.nombre.trim(),
        tipo: 'otro',
        estado: 'prospecto',
        es_cliente: true,
        pendiente_revision: !formCrear.cif.trim(),
      };
      if (formCrear.cif.trim()) body.cif = formCrear.cif.trim();
      if (formCrear.dominio.trim()) body.dominio_email = formCrear.dominio.trim();
      const ent = await req('/api/entidades', { method: 'POST', body: JSON.stringify(body) });
      toast.success(`Empresa "${ent.razon_social}" creada`);
      await _doVincular(ent.id, ent.nombre_comercial || ent.razon_social, !!formCrear.dominio.trim());
    } catch (e) { toast.error(`Crear empresa: ${e.message}`); }
    finally { setCreando(false); }
  };

  // ── Contacto ────────────────────────────────────────────────
  const crearContacto = async () => {
    if (!formContacto.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setCreandoContacto(true);
    let contactoId = null;
    try {
      const c = await req('/api/contactos', {
        method: 'POST',
        body: JSON.stringify({
          nombre: formContacto.nombre.trim(),
          apellidos: formContacto.apellidos.trim() || undefined,
          cargo: formContacto.cargo.trim() || undefined,
          email_trabajo: msg.from_address,
          entidad_id: contactoEmpresaId || entidadIdFinal || undefined,
          origen: 'enriquecimiento_correo',
        }),
      });
      contactoId = c?.id || null;
      toast.success('Contacto añadido');
      // Persistir el vínculo contacto↔correo en el backend
      if (contactoId) {
        try {
          await req(`/api/correo/mensajes/${encodeURIComponent(msg.message_id)}/vincular`, {
            method: 'PATCH',
            body: JSON.stringify({ contacto_id: contactoId }),
          });
        } catch (_) { /* no crítico */ }
      }
    } catch (e) {
      toast.error(`Crear contacto: ${e.message}`);
    } finally {
      setCreandoContacto(false);
      onVinculado(msg.message_id, contactoEmpresaId || entidadIdFinal, entidadNombreFinal, contactoId);
    }
  };

  const omitirContacto = () => onVinculado(msg.message_id, entidadIdFinal, entidadNombreFinal, null);

  // Saltar directamente al formulario de contacto sin vincular empresa primero
  // (para dominios genéricos o cuando el usuario quiere crear el contacto sin empresa)
  const irAContactoDirecto = () => setModo('contacto');

  const buscarEmpresaContacto = (q) => {
    setContactoEmpresaQuery(q);
    if (!contactoEmpresaId) setContactoEmpresaNombre(q);
    clearTimeout(contactoEmpresaTimer.current);
    if (!q.trim()) { setContactoEmpresaResultados([]); return; }
    contactoEmpresaTimer.current = setTimeout(async () => {
      setContactoEmpresaBuscando(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(q.trim())}&limit=5`);
        setContactoEmpresaResultados(Array.isArray(d) ? d : (d.items || []));
      } catch (_) { setContactoEmpresaResultados([]); }
      finally { setContactoEmpresaBuscando(false); }
    }, 300);
  };

  // ── Ignorar ─────────────────────────────────────────────────
  const ignorar = async () => {
    setIgnorando(true);
    try {
      await req(`/api/correo/mensajes/${encodeURIComponent(msg.message_id)}/ignorar`, { method: 'PATCH' });
      onIgnorado(msg.message_id);
    } catch (e) {
      toast.error(`Ignorar: ${e.message}`);
      setIgnorando(false);
    }
  };

  // ── Render: modo contacto (compacto) ────────────────────────
  if (modo === 'contacto') {
    return (
      <div className="px-4 py-2 bg-blue-50 border-b border-blue-200" data-testid="vincular-contacto-panel">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className="text-xs font-semibold text-blue-800">Añadir como contacto</span>
          <span className="text-[10px] text-blue-500 truncate">{msg.from_address}</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-1.5">
          <Input className="h-7 text-xs" value={formContacto.nombre}
            onChange={(e) => setFormContacto((f) => ({ ...f, nombre: e.target.value }))}
            placeholder="Nombre *" data-testid="contacto-nombre" />
          <Input className="h-7 text-xs" value={formContacto.apellidos}
            onChange={(e) => setFormContacto((f) => ({ ...f, apellidos: e.target.value }))}
            placeholder="Apellidos" data-testid="contacto-apellidos" />
          <Input className="h-7 text-xs" value={formContacto.cargo}
            onChange={(e) => setFormContacto((f) => ({ ...f, cargo: e.target.value }))}
            placeholder="Cargo" data-testid="contacto-cargo" />
        </div>
        {/* Empresa — siempre visible: muestra la vinculada o el buscador */}
        <div className="relative mb-1.5">
          {entidadIdFinal && !contactoEmpresaId ? (
            <div className="flex items-center gap-2 h-7 px-2 rounded border border-emerald-200 bg-emerald-50 text-xs">
              <span className="flex-1 text-emerald-800 font-medium truncate">{entidadNombreFinal || 'Empresa vinculada'}</span>
              <button type="button" className="text-[10px] text-ink-400 hover:text-ink-700 shrink-0"
                onClick={() => { setEntidadIdFinal(null); setEntidadNombreFinal(''); }}
                title="Cambiar empresa">✕ Cambiar</button>
            </div>
          ) : (
            <>
              <Input className="h-7 text-xs"
                value={contactoEmpresaId ? contactoEmpresaNombre : contactoEmpresaQuery}
                placeholder="Empresa (buscar o dejar en blanco)…"
                onChange={(e) => { setContactoEmpresaId(null); buscarEmpresaContacto(e.target.value); }}
                onFocus={() => { if (contactoEmpresaId) { setContactoEmpresaId(null); setContactoEmpresaQuery(contactoEmpresaNombre); } }}
                data-testid="contacto-empresa" />
              {contactoEmpresaBuscando && <p className="text-[10px] text-ink-400 mt-0.5">Buscando…</p>}
              {contactoEmpresaResultados.length > 0 && !contactoEmpresaId && (
                <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded shadow-md mt-0.5 max-h-32 overflow-y-auto">
                  {contactoEmpresaResultados.map((r) => (
                    <button key={r.id} type="button"
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-brand-50 border-b border-ink-50 last:border-0"
                      onClick={() => { setContactoEmpresaId(r.id); setContactoEmpresaNombre(r.nombre_comercial || r.razon_social); setContactoEmpresaResultados([]); }}>
                      {r.nombre_comercial || r.razon_social}
                      {r.cif && <span className="ml-1 text-ink-400">{r.cif}</span>}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-between items-center">
          <button type="button" className="text-[11px] text-ink-400 hover:text-ink-600"
            onClick={omitirContacto}>Omitir</button>
          <Button size="sm" className="h-7 text-xs" onClick={crearContacto}
            disabled={!formContacto.nombre.trim() || creandoContacto} data-testid="contacto-confirmar">
            {creandoContacto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Añadir contacto →'}
          </Button>
        </div>
      </div>
    );
  }

  // ── Render: modo crear empresa ──────────────────────────────
  if (modo === 'crear') {
    return (
      <div className="px-5 py-3 bg-orange-50 border-b border-orange-200" data-testid="vincular-crear-panel">
        <div className="flex items-center gap-2 mb-3">
          <button type="button" className="text-[11px] text-ink-500 hover:text-ink-700"
            onClick={() => setModo('buscar')}>← Volver</button>
          <span className="text-xs font-semibold text-orange-800">Nueva empresa</span>
        </div>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Nombre *</label>
            <Input className="h-8 text-sm" placeholder="Razón social" value={formCrear.nombre}
              onChange={(e) => { setFormCrear((f) => ({ ...f, nombre: e.target.value })); verificarNombre(e.target.value); }}
              data-testid="crear-empresa-nombre" />
            {(buscandoNombre || nombreSimilares.length > 0) && (
              <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-[11px]">
                {buscandoNombre && <p className="text-ink-400">Verificando nombre…</p>}
                {!buscandoNombre && nombreSimilares.length > 0 && (
                  <>
                    <p className="text-amber-800 font-semibold mb-1">⚠ Ya existen empresas similares:</p>
                    <ul className="space-y-1">
                      {nombreSimilares.map((s) => (
                        <li key={s.id} className="flex items-center justify-between gap-2">
                          <span className="text-ink-700">
                            {s.nombre_comercial || s.razon_social}
                            {s.cif && <span className="ml-1.5 text-ink-400">{s.cif}</span>}
                          </span>
                          <button type="button" className="text-brand-600 underline text-[10px] shrink-0"
                            onClick={() => { setSeleccionada({ id: s.id, nombre: s.nombre_comercial || s.razon_social }); setNombreSimilares([]); setModo('buscar'); }}>
                            Usar esta
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">CIF / NIF <span className="text-amber-600">(recomendado)</span></label>
            <Input className={`h-8 text-sm ${cifExistente ? 'border-red-400' : ''}`}
              placeholder="B12345678" value={formCrear.cif}
              onChange={(e) => { setFormCrear((f) => ({ ...f, cif: e.target.value })); verificarCif(e.target.value); }}
              data-testid="crear-empresa-cif" />
            {buscandoCif && <p className="text-[10px] text-ink-400 mt-0.5">Verificando CIF…</p>}
            {cifExistente && (
              <div className="mt-1 p-1.5 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
                Ya existe: <strong>{cifExistente.nombre}</strong>
                <button type="button" className="ml-2 underline text-red-700"
                  onClick={() => { setSeleccionada({ id: cifExistente.id, nombre: cifExistente.nombre }); setModo('buscar'); }}>
                  Usar esta empresa
                </button>
              </div>
            )}
            {!formCrear.cif.trim() && !cifExistente && (
              <p className="text-[10px] text-amber-600 mt-0.5">
                Sin CIF/NIF la empresa quedará pendiente de enriquecimiento y no aparecerá en la lista principal de clientes.
              </p>
            )}
          </div>
          <div>
            <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Dominio email</label>
            <Input className="h-8 text-sm" placeholder={dominioGenerico ? 'empresa.com' : dominio}
              value={formCrear.dominio}
              onChange={(e) => setFormCrear((f) => ({ ...f, dominio: e.target.value }))}
              data-testid="crear-empresa-dominio" />
            <p className="text-[10px] text-ink-400 mt-0.5">Futuros correos de este dominio se vincularán automáticamente</p>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-700"
            onClick={crearEmpresa} disabled={!formCrear.nombre.trim() || !!cifExistente || creando}
            data-testid="crear-empresa-confirmar">
            {creando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Crear y vincular'}
          </Button>
        </div>
      </div>
    );
  }

  // ── Render: modo buscar (compacto) ──────────────────────────
  return (
    <div className="px-4 py-2 bg-orange-50 border-b border-orange-200" data-testid="vincular-empresa-panel">
      {/* Fila superior: título + botón IA inline */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-orange-800">Vincular a empresa</span>
          {dominio && !dominioGenerico && (
            <span className="text-[10px] text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">@{dominio}</span>
          )}
        </div>
        {!dominioGenerico && dominio && (
          <button type="button"
            className="flex items-center gap-1 text-[11px] text-violet-700 hover:text-violet-900 font-medium disabled:opacity-40 shrink-0"
            onClick={analizarConIA} disabled={analizandoIA}
            data-testid="btn-analizar-ia">
            {analizandoIA ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {analizandoIA ? 'Buscando…' : 'Identificar con IA'}
          </button>
        )}
      </div>
      {iaMsg && (
        <p className={`text-[10px] mb-1.5 ${iaMsg.startsWith('✓') ? 'text-emerald-600 font-medium' : 'text-amber-600'}`}>{iaMsg}</p>
      )}

      {/* Búsqueda + botón vincular en la misma fila */}
      <div className="flex gap-2">
        <div className="relative flex-1">
        <Input className="h-8 text-sm" placeholder="Buscar empresa por nombre…"
          value={seleccionada ? seleccionada.nombre : query}
          onChange={(e) => buscar(e.target.value)}
          onFocus={() => { if (seleccionada) { setSeleccionada(null); setResultados([]); } }}
          data-testid="vincular-buscar-empresa" />
        {(resultados.length > 0 || buscando) && !seleccionada && (
          <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded shadow-md mt-0.5 max-h-44 overflow-y-auto">
            {buscando && <div className="px-3 py-2 text-xs text-ink-500">Buscando…</div>}
            {resultados.map((r) => (
              <button key={r.id} type="button"
                className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b border-ink-50 last:border-0"
                onClick={() => { setSeleccionada({ id: r.id, nombre: r.nombre_comercial || r.razon_social || r.id }); setResultados([]); }}
                data-testid={`vincular-resultado-${r.id}`}>
                <span className="font-medium text-ink-900">{r.nombre_comercial || r.razon_social}</span>
                {r.cif && <span className="ml-1 text-ink-400">{r.cif}</span>}
              </button>
            ))}
          </div>
        )}
        </div>
        <Button size="sm" className="h-8 text-xs bg-orange-600 hover:bg-orange-700 shrink-0"
          onClick={confirmarVincular} disabled={!seleccionada || vinculando}
          data-testid="vincular-confirmar">
          {vinculando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Vincular'}
        </Button>
      </div>
      {/* Fila inferior: checkbox + links secundarios */}
      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-3">
          {!dominioGenerico && dominio && seleccionada && (
            <label className="flex items-center gap-1 text-[10px] text-orange-700 cursor-pointer">
              <input type="checkbox" checked={guardarDominio} onChange={(e) => setGuardarDominio(e.target.checked)} data-testid="vincular-guardar-dominio" />
              Recordar @{dominio}
            </label>
          )}
          {!dominioGenerico && (
            <button type="button" className="text-[11px] text-brand-600 hover:underline"
              onClick={() => setModo('crear')} data-testid="vincular-crear-nueva">
              + Crear empresa
            </button>
          )}
          <button type="button" className="text-[11px] text-ink-400 hover:text-ink-600 disabled:opacity-50"
            onClick={ignorar} disabled={ignorando} data-testid="vincular-ignorar">
            {ignorando && <Loader2 className="w-3 h-3 animate-spin inline mr-0.5" />}
            Ignorar
          </button>
        </div>
      </div>
      {/* Para dominios genéricos (gmail, outlook…): acceso directo a crear contacto */}
      {dominioGenerico && sugerirContacto && (
        <div className="mt-1.5 pt-1.5 border-t border-orange-200 flex items-center justify-between">
          <span className="text-[10px] text-ink-500">
            Correo personal · ¿Conoces a este contacto?
          </span>
          <button type="button"
            className="text-[11px] text-blue-600 hover:text-blue-800 font-medium hover:underline"
            onClick={irAContactoDirecto}
            data-testid="vincular-contacto-directo">
            Añadir como contacto →
          </button>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Componente principal
// ════════════════════════════════════════════════════════════════════
export default function CorreosGmail() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [cuentas, setCuentas] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCuenta, setFiltroCuenta] = useState('all');
  const [soloNoLeidos, setSoloNoLeidos] = useState(false);
  const [soloOportunidades, setSoloOportunidades] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaTerm, setBusquedaTerm] = useState('');
  const [activeThreadKey, setActiveThreadKey] = useState(null);
  const [activeBody, setActiveBody] = useState({ loading: false, data: null });
  const [composer, setComposer] = useState({
    open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false,
    subject: '', body: '', cuenta_id: '', adjuntos: [], in_reply_to: '', references: '', original_message_id: '',
  });
  const [mostrarAtajos, setMostrarAtajos] = useState(false);
  const [tareasIA, setTareasIA] = useState({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false, ediciones: {} });
  const [crearTareaRevision, setCrearTareaRevision] = useState({
    open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), ediciones: {}, creando: false, manualOnly: false,
  });
  const [sugerencias, setSugerencias] = useState({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] });
  const [sincronizando, setSincronizando] = useState(false);
  // Iter68 — modal fullscreen, filtro urgentes, equipo y borrador local
  const [fullscreenMsg, setFullscreenMsg] = useState(null);
  const [soloUrgentes, setSoloUrgentes] = useState(false);
  const [equipo, setEquipo] = useState([]);
  const searchInputRef = useRef(null);

  // ─── Carga ────────────────────────────────────────────────
  const cuentasMap = useMemo(() => Object.fromEntries(cuentas.map((c) => [String(c.id), c])), [cuentas]);

  const cuentaIdDefectoParaMensaje = useCallback((m) => {
    const cid = m?.cuenta_id;
    if (cid != null && cuentasMap[String(cid)]?.id) return String(cuentasMap[String(cid)].id);
    if (cid != null && cuentas.some((x) => String(x.id) === String(cid))) return String(cid);
    return cuentas[0]?.id ? String(cuentas[0].id) : '';
  }, [cuentasMap, cuentas]);

  const cargarCuentas = async () => {
    try {
      const d = await req('/api/correo/cuentas');
      setCuentas(Array.isArray(d) ? d : (d.items || []));
    } catch (e) { toast.error(`Cuentas: ${e.message}`); }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filtroCuenta !== 'all') params.set('cuenta_id', filtroCuenta);
      if (soloNoLeidos) params.set('solo_no_leidos', 'true');
      if (busquedaTerm) params.set('q', busquedaTerm);
      const d = await req(`/api/correo/sincronizados?${params}`);
      setItems(d.items || []);
    } catch (e) { toast.error(`Bandeja: ${e.message}`); }
    finally { setLoading(false); }
  }, [filtroCuenta, soloNoLeidos, busquedaTerm]);

  // Iter68 — cargar miembros del equipo (admin/dir_comercial/comercial) para "Asignado a"
  const cargarEquipo = useCallback(async () => {
    try {
      const u = await req('/api/usuarios');
      const lista = (Array.isArray(u) ? u : (u.items || [])).filter(
        (x) => ['admin', 'dir_comercial', 'comercial'].includes(x.rol),
      );
      setEquipo(lista);
    } catch (_) { /* opcional, no crítico */ }
  }, []);

  useEffect(() => { cargarCuentas(); cargarEquipo(); }, [cargarEquipo]);
  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const t = setTimeout(() => setBusquedaTerm(busqueda), 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  // ─── Thread grouping ────────────────────────────────────
  const threads = useMemo(() => {
    let base = items;
    if (soloOportunidades) base = base.filter((m) => m.es_oportunidad);
    if (soloUrgentes) base = base.filter((m) => m.urgente);
    return groupByThread(base);
  }, [items, soloOportunidades, soloUrgentes]);
  const oportunidadesCount = useMemo(() => items.filter((m) => m.es_oportunidad).length, [items]);
  const urgentesCount = useMemo(() => items.filter((m) => m.urgente).length, [items]);
  const sinVincularCount = useMemo(() => items.filter((m) => !m.vinculado_entidad_id && !m.ignorado).length, [items]);

  // Última sincronización: la más reciente de todas las cuentas conectadas
  const ultimaSync = useMemo(() => {
    const fechas = cuentas.map((c) => c.ultima_sync).filter(Boolean);
    if (!fechas.length) return null;
    return fechas.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
  }, [cuentas]);

  const activeThread = useMemo(
    () => threads.find((t) => t.key === activeThreadKey) || null,
    [threads, activeThreadKey],
  );
  const activePanelMessageIdRef = useRef(null);
  useEffect(() => {
    activePanelMessageIdRef.current = activeThread?.last?.message_id ?? null;
  }, [activeThread?.last?.message_id]);

  const referencesCadenaParaMensaje = useCallback((msg) => {
    if (!msg?.message_id) return '';
    const thr = threads.find((t) => t.items.some((it) => it.message_id === msg.message_id));
    if (!thr?.items?.length) return '';
    const asc = [...thr.items].sort((a, b) => new Date(a.received_at) - new Date(b.received_at));
    return asc.map((x) => x.message_id).filter(Boolean).join(' ');
  }, [threads]);

  // ─── Seleccionar + marcar leído + cargar body ──────────────
  const seleccionarThread = useCallback(async (thread) => {
    setActiveThreadKey(thread.key);
    const m = thread.last;
    // Cargar body completo
    setActiveBody({ loading: true, data: null });
    try {
      const cuenta = cuentasMap[String(m.cuenta_id)];
      if (cuenta) {
        const d = await req(`/api/correo/cuentas/${cuenta.id}/mensajes/${encodeURIComponent(m.message_id)}/body`);
        setActiveBody({ loading: false, data: d });
      } else {
        setActiveBody({ loading: false, data: { body_html: '', body_text: m.preview || '' } });
      }
    } catch (e) {
      console.error('body', e);
      setActiveBody({ loading: false, data: { body_html: '', body_text: m.preview || '' } });
    }
    // Marcar leído si no lo estaba
    if (m.is_read === false) {
      try {
        await req(`/api/correo/mensajes/${encodeURIComponent(m.message_id)}/leido?leido=true`, { method: 'PATCH' });
        setItems((prev) => prev.map((x) => x.message_id === m.message_id ? { ...x, is_read: true } : x));
      } catch (_) { /* silent */ }
    }
  }, [cuentasMap]);

  const asegurarCuerpoCargadoParaMensaje = useCallback(async (m) => {
    const cuenta = cuentasMap[String(m?.cuenta_id)];
    if (!cuenta || !m?.message_id) return;
    const mid = m.message_id;
    const mismoPanel = activePanelMessageIdRef.current === mid;
    if (mismoPanel && activeBody.data && !activeBody.loading) return;
    try {
      const d = await req(`/api/correo/cuentas/${cuenta.id}/mensajes/${encodeURIComponent(mid)}/body`);
      if (activePanelMessageIdRef.current === mid) {
        setActiveBody({ loading: false, data: d });
      }
    } catch (e) {
      console.error('body preload crear-tarea', e);
      if (activePanelMessageIdRef.current === mid) {
        setActiveBody({ loading: false, data: { body_html: '', body_text: m.preview || '' } });
      }
    }
  }, [cuentasMap, activeBody.data, activeBody.loading]);

  // Abrir hilo desde ?message_id= (p. ej. enlace desde tarea)
  useEffect(() => {
    const mid = searchParams.get('message_id');
    if (!mid || loading || !items.length) return;
    const thr = threads.find((t) => t.items.some((it) => it.message_id === mid));
    if (!thr) return;
    let cancelled = false;
    (async () => {
      await seleccionarThread(thr);
      if (cancelled) return;
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('message_id');
        return p;
      }, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [searchParams, loading, items, threads, seleccionarThread, setSearchParams]);

  // Composer: asegurar cuenta_id válido cuando carguen cuentas o falte id
  useEffect(() => {
    if (!composer.open || !cuentas.length) return;
    const ok = composer.cuenta_id && cuentas.some((c) => String(c.id) === String(composer.cuenta_id));
    if (!ok) setComposer((c) => ({ ...c, cuenta_id: String(cuentas[0].id) }));
  }, [composer.open, composer.cuenta_id, cuentas]);

  // ─── Atajos teclado (solo desktop) ─────────────────────
  useEffect(() => {
    const isDesktop = () => window.innerWidth >= 768;
    const onKey = (e) => {
      if (!isDesktop()) return;
      // Ignorar si estamos escribiendo en input/textarea
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) {
        if (e.key === 'Escape') e.target.blur();
        return;
      }
      if (composer.open || sugerencias.open || tareasIA.open || crearTareaRevision.open) return;

      if (e.key === '?') {
        e.preventDefault();
        setMostrarAtajos((x) => !x);
        return;
      }
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (!threads.length) return;
      const idx = threads.findIndex((t) => t.key === activeThreadKey);
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min(idx + 1, threads.length - 1);
        if (next >= 0) seleccionarThread(threads[next]);
        return;
      }
      if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = Math.max(idx - 1, 0);
        seleccionarThread(threads[prev]);
        return;
      }
      if (!activeThread) return;
      if (e.key === 'r') {
        e.preventDefault();
        responder(activeThread.last);
        return;
      }
      if (e.key === 'u' || e.key === 'Escape') {
        e.preventDefault();
        setActiveThreadKey(null);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [threads, activeThreadKey, composer.open, sugerencias.open, tareasIA.open, crearTareaRevision.open, activeThread, seleccionarThread]);

  // ─── Acciones sobre correo activo ──────────────────────
  const responder = (m, cuerpoExt) => {
    const cuerpo = cuerpoExt || activeBody?.data?.body_text || m.preview || '';
    setComposer({
      open: true,
      to: m.from_address ? [m.from_address] : [],
      cc: [], bcc: [], showCc: false, showBcc: false,
      subject: m.subject?.startsWith('Re:') ? m.subject : `Re: ${m.subject || ''}`,
      body: `\n\n--- Mensaje original ---\nDe: ${m.from_name || m.from_address}\nAsunto: ${m.subject || ''}\n\n${cuerpo}`,
      cuenta_id: cuentaIdDefectoParaMensaje(m),
      adjuntos: [],
      in_reply_to: m.message_id || '',
      references: referencesCadenaParaMensaje(m),
      original_message_id: '',
    });
  };

  // Iter68 — Responder a todos: incluye to+cc, excluye la cuenta propia
  const responderTodos = (m, cuerpoExt) => {
    const cuenta = cuentasMap[String(m.cuenta_id)];
    const cuerpo = cuerpoExt || activeBody?.data?.body_text || m.preview || '';
    const propio = (cuenta?.email || '').toLowerCase();
    const dest = new Set();
    if (m.from_address) dest.add(m.from_address.toLowerCase());
    for (const a of (m.to_addresses || [])) dest.add(String(a).toLowerCase());
    const cc = new Set();
    for (const a of (m.cc_addresses || [])) cc.add(String(a).toLowerCase());
    if (propio) { dest.delete(propio); cc.delete(propio); }
    setComposer({
      open: true,
      to: Array.from(dest),
      cc: Array.from(cc), bcc: [], showCc: cc.size > 0, showBcc: false,
      subject: m.subject?.startsWith('Re:') ? m.subject : `Re: ${m.subject || ''}`,
      body: `\n\n--- Mensaje original ---\nDe: ${m.from_name || m.from_address}\nAsunto: ${m.subject || ''}\n\n${cuerpo}`,
      cuenta_id: cuentaIdDefectoParaMensaje(m),
      adjuntos: [],
      in_reply_to: m.message_id || '',
      references: referencesCadenaParaMensaje(m),
      original_message_id: '',
    });
  };

  // Iter68 — Reenviar: composer con "Fwd:" y separador del mensaje original
  const reenviar = (m, cuerpoExt) => {
    const cuerpo = cuerpoExt || activeBody?.data?.body_text || m.preview || '';
    setComposer({
      open: true,
      to: [], cc: [], bcc: [], showCc: false, showBcc: false,
      subject: m.subject?.toLowerCase().startsWith('fwd:') ? m.subject : `Fwd: ${m.subject || ''}`,
      body: `\n\n-------- Mensaje original --------\nDe: ${m.from_name || m.from_address}\nAsunto: ${m.subject || ''}\nFecha: ${m.received_at ? new Date(m.received_at).toLocaleString('es-ES') : ''}\n\n${cuerpo}`,
      cuenta_id: cuentaIdDefectoParaMensaje(m),
      adjuntos: [],
      in_reply_to: '',
      references: '',
      original_message_id: m.message_id || '',
    });
  };

  // Iter68 — Toggle urgente (PATCH backend + actualización local)
  const toggleUrgente = async (m) => {
    const nuevo = !m.urgente;
    try {
      await req(`/api/correo/mensajes/${encodeURIComponent(m.message_id)}/urgente?urgente=${nuevo}`, { method: 'PATCH' });
      setItems((prev) => prev.map((x) => x.message_id === m.message_id ? { ...x, urgente: nuevo } : x));
      if (fullscreenMsg?.message_id === m.message_id) setFullscreenMsg({ ...fullscreenMsg, urgente: nuevo });
      toast.success(nuevo ? 'Marcado como urgente' : 'Quitada urgencia');
    } catch (e) { toast.error(e.message); }
  };

  const abrirFullscreen = (m) => setFullscreenMsg(m);
  const cerrarFullscreen = () => setFullscreenMsg(null);

  // Sprint 1 — Actualiza estado local tras vincular empresa y/o contacto a un correo
  const vincularCorreo = (messageId, entidadId, entidadNombre, contactoId) => {
    setItems((prev) => prev.map((x) => {
      if (x.message_id !== messageId) return x;
      return {
        ...x,
        ...(entidadId ? { vinculado_entidad_id: entidadId, vinculado_entidad_nombre: entidadNombre || x.vinculado_entidad_nombre || '' } : {}),
        ...(contactoId ? { vinculado_contacto_id: contactoId } : {}),
      };
    }));
    if (entidadNombre && !contactoId) toast.success(`Vinculado a ${entidadNombre}`);
  };

  // Sprint 1 — Marca un correo como ignorado (excluye del contador y del panel)
  const ignorarCorreo = (messageId) => {
    setItems((prev) => prev.map((x) =>
      x.message_id === messageId ? { ...x, ignorado: true } : x,
    ));
  };

  // ────────────────────────────────────────────────────────────
  // Iter68 — Borrador autosalvado en localStorage
  // ────────────────────────────────────────────────────────────
  const draftStartRef = useRef(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // Recuperar borrador al abrir un composer vacío
  useEffect(() => {
    if (!composer.open) { draftStartRef.current = null; return; }
    if (draftStartRef.current) return; // ya inicializado
    draftStartRef.current = Date.now();
    // Solo recuperar si el composer está vacío (no estamos respondiendo/reenviando algo concreto)
    const composerVacio = !composer.subject && !composer.body && composer.to.length === 0;
    if (composerVacio) {
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const d = JSON.parse(raw);
          if (d && (d.subject || d.body || (d.to || []).length > 0)) {
            setComposer((c) => ({
              ...c,
              to: d.to || [], cc: d.cc || [], bcc: d.bcc || [],
              showCc: (d.cc || []).length > 0, showBcc: (d.bcc || []).length > 0,
              subject: d.subject || '', body: d.body || '',
              cuenta_id: d.cuenta_id || c.cuenta_id || (cuentas[0]?.id ? String(cuentas[0].id) : ''),
            }));
            toast.info('Borrador anterior recuperado');
          }
        }
      } catch (_) { /* noop */ }
    }
  }, [composer.open, composer.subject, composer.body, composer.to.length]);

  // Guardar borrador cada 30s si llevamos >10s en el composer
  useEffect(() => {
    if (!composer.open) return;
    const t = setInterval(() => {
      const elapsed = Date.now() - (draftStartRef.current || Date.now());
      if (elapsed < 10000) return;
      const tieneContenido = composer.subject || composer.body || composer.to.length > 0;
      if (!tieneContenido) return;
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          to: composer.to, cc: composer.cc, bcc: composer.bcc,
          subject: composer.subject, body: composer.body, cuenta_id: composer.cuenta_id,
        }));
        setDraftSavedAt(new Date());
      } catch (_) { /* localStorage lleno */ }
    }, 30000);
    return () => clearInterval(t);
  }, [composer.open, composer.subject, composer.body, composer.to, composer.cc, composer.bcc, composer.cuenta_id]);

  const descartarBorrador = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* noop */ }
    setDraftSavedAt(null);
    setComposer({
      open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false,
      subject: '', body: '', cuenta_id: '', adjuntos: [], in_reply_to: '', references: '', original_message_id: '',
    });
    toast.success('Borrador descartado');
  };

  // cuenta_id efectiva: usa el estado o cae al primero disponible (evita que el select
  // muestre una cuenta visualmente mientras el estado queda vacío y deshabilita el botón)
  const cuentaIdEfectiva = composer.cuenta_id
    || (cuentas.length > 0 ? String(cuentas[0].id) : '');

  const enviar = async () => {
    if (!composer.to.length) { toast.error('Faltan destinatarios'); return; }
    if (!cuentaIdEfectiva) { toast.error('Selecciona la cuenta de envío'); return; }
    if (!composer.subject?.trim()) { toast.error('El asunto es obligatorio'); return; }
    if (!composer.body?.trim()) { toast.error('El mensaje no puede estar vacío'); return; }
    try {
      const fd = new FormData();
      fd.append('to', JSON.stringify(composer.to));
      fd.append('subject', composer.subject);
      fd.append('body', composer.body);
      if (composer.cc.length > 0) fd.append('cc', JSON.stringify(composer.cc));
      const esRespuesta = !!(composer.in_reply_to && String(composer.in_reply_to).trim());
      const esReenvio = !!(composer.original_message_id && String(composer.original_message_id).trim()) && !esRespuesta;
      if (!esRespuesta && !esReenvio && composer.bcc.length > 0) fd.append('bcc', JSON.stringify(composer.bcc));
      if (esRespuesta) {
        fd.append('in_reply_to', String(composer.in_reply_to).trim());
        if (composer.references) fd.append('references', composer.references);
      }
      if (esReenvio) {
        fd.append('original_message_id', String(composer.original_message_id).trim());
      }
      for (const f of (composer.adjuntos || [])) fd.append('attachments', f, f.name);
      const token = localStorage.getItem('token');
      const path = esRespuesta ? 'reply' : (esReenvio ? 'forward' : 'enviar');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/correo/cuentas/${cuentaIdEfectiva}/${path}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      const ad = composer.adjuntos?.length || 0;
      toast.success(esReenvio ? `Reenvío enviado${ad ? ` (${ad} adjunto(s) nuevo(s))` : ''}` : `Correo enviado${ad ? ` con ${ad} adjunto(s)` : ''}`);
      // Iter68 — limpiar borrador local al enviar OK
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* noop */ }
      setDraftSavedAt(null);
      setComposer({
        open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false,
        subject: '', body: '', cuenta_id: '', adjuntos: [], in_reply_to: '', references: '', original_message_id: '',
      });
      setTimeout(cargar, 800);
    } catch (e) { toast.error(`Envío: ${e.message}`); }
  };

  const sincronizarTodas = async () => {
    setSincronizando(true);
    let totalCorreos = 0;
    let errores = 0;
    try {
      for (const c of cuentas) {
        try {
          const r = await req(`/api/correo/cuentas/${c.id}/sincronizar-todo`, { method: 'POST' });
          const fases = r.fases || {};
          totalCorreos += fases.correos?.detalle?.total ?? fases.correos?.detalle?.inbox ?? 0;
        } catch (e) {
          console.error(`[sync] cuenta ${c.email}:`, e);
          errores++;
        }
      }
      if (errores > 0 && totalCorreos === 0) {
        toast.error(`Sincronización falló en ${errores} cuenta(s)`);
      } else {
        toast.success(`Sincronización completada · ${totalCorreos} correos${errores ? ` · ${errores} error(es)` : ''}`);
      }
      cargar();
    } finally { setSincronizando(false); }
  };

  // ─── Sugerencias / tareas IA ─────────────────────────────
  const abrirSugerencias = async (m) => {
    setSugerencias({ open: true, loading: true, msg: m, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] });
    try {
      const r = await req('/api/correo/ai/sugerir-respuestas', {
        method: 'POST', body: JSON.stringify({ message_id: m.message_id }),
      });
      setSugerencias({
        open: true, loading: false, msg: m,
        borradores: r.borradores || [], perfilAplicado: !!r.perfil_aplicado,
        contexto: r.contexto || null, memoriasRelevantes: r.memorias_relevantes || [],
      });
    } catch (e) { toast.error(`Gemini: ${e.message}`); setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] }); }
  };
  const cerrarSugerencias = () => setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] });
  const usarBorrador = (b) => {
    const m = sugerencias.msg;
    setComposer({
      open: true, to: m?.from_address ? [m.from_address] : [], cc: [], bcc: [], showCc: false, showBcc: false,
      subject: b.asunto || (m?.subject?.startsWith('Re:') ? m.subject : `Re: ${m?.subject || ''}`),
      body: b.cuerpo || '',
      cuenta_id: cuentaIdDefectoParaMensaje(m), adjuntos: [],
      in_reply_to: m?.message_id || '',
      references: m ? referencesCadenaParaMensaje(m) : '',
      original_message_id: '',
    });
    cerrarSugerencias();
  };

  const extraerTareas = async (m) => {
    setTareasIA({ open: true, loading: true, msg: m, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false, ediciones: {} });
    try {
      const r = await req('/api/correo/ai/extraer-tareas', {
        method: 'POST', body: JSON.stringify({ message_id: m.message_id }),
      });
      const propuestas = r.tareas_propuestas || [];
      // Iter68 — pre-rellenar ediciones con valores por defecto
      const ediciones = {};
      propuestas.forEach((p, i) => {
        ediciones[i] = {
          vencimiento: p.fecha_propuesta || p.vencimiento || '',
          prioridad: p.prioridad || 'media',
          entidad_id: m?.vinculado_entidad_id || '',
          asignado_user_id: user?.id || '',
        };
      });
      setTareasIA({ open: true, loading: false, msg: m, propuestas, seleccionadas: new Set(propuestas.map((_, i) => i)), resumen: r.resumen_correo || '', creando: false, ediciones });
    } catch (e) { toast.error(`IA: ${e.message}`); setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false, ediciones: {} }); }
  };
  const toggleTareaSel = (i) => {
    setTareasIA((t) => {
      const s = new Set(t.seleccionadas);
      if (s.has(i)) s.delete(i); else s.add(i);
      return { ...t, seleccionadas: s };
    });
  };
  // Iter68 — Editor inline de campos por tarea propuesta
  const editarTareaCampo = (i, campo, valor) => {
    setTareasIA((t) => ({
      ...t,
      ediciones: { ...t.ediciones, [i]: { ...(t.ediciones[i] || {}), [campo]: valor } },
    }));
  };

  const crearTareasSeleccionadas = async () => {
    const m = tareasIA.msg;
    setTareasIA((t) => ({ ...t, creando: true }));
    let ok = 0, fail = 0;
    for (let i = 0; i < tareasIA.propuestas.length; i++) {
      if (!tareasIA.seleccionadas.has(i)) continue;
      const t = tareasIA.propuestas[i];
      const ed = tareasIA.ediciones[i] || {};
      try {
        await req('/api/tareas', {
          method: 'POST',
          body: JSON.stringify({
            titulo: ed.titulo || t.titulo,
            descripcion: (ed.descripcion !== undefined ? ed.descripcion : (t.descripcion || '')) + (m?.subject ? `\n\n📧 Origen: "${m.subject}"` : ''),
            entidad_id: ed.entidad_id || m?.vinculado_entidad_id || null,
            contacto_id: m?.vinculado_contacto_id || null,
            vencimiento: ed.vencimiento || null,
            prioridad: ed.prioridad || 'media',
            asignado_user_id: ed.asignado_user_id || user?.id,
            urgente: ed.urgente ?? t.urgente ?? false,
            importante: ed.importante ?? t.importante ?? false,
            // Iter68 — trazabilidad correo
            correo_id: m?.message_id || null,
            correo_asunto: m?.subject || null,
          }),
        });
        ok++;
      } catch (_) { fail++; }
    }
    if (ok > 0) toast.success(`${ok} tarea(s) creada(s)${fail ? ` · ${fail} fallaron` : ''}`);
    else if (fail > 0) toast.error(`No se crearon tareas (${fail} fallos)`);
    setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false, ediciones: {} });
  };

  const enriquecerEdicionesEntidadNombre = async (edicionesIn) => {
    const ediciones = { ...edicionesIn };
    await Promise.all(Object.keys(ediciones).map(async (k) => {
      const eid = String((ediciones[k].entidad_id || '')).trim();
      if (!eid) return;
      try {
        const ent = await req(`/api/entidades/${encodeURIComponent(eid)}`);
        const nombre = ent?.nombre_comercial || ent?.razon_social || '';
        ediciones[k] = { ...ediciones[k], entidad_nombre: nombre || eid };
      } catch (_) {
        ediciones[k] = { ...ediciones[k], entidad_nombre: eid };
      }
    }));
    return ediciones;
  };

  const abrirCrearTareaRevision = async (m) => {
    setCrearTareaRevision({
      open: true, loading: true, msg: m, propuestas: [], seleccionadas: new Set(), ediciones: {}, creando: false, manualOnly: false,
    });
    try {
      await asegurarCuerpoCargadoParaMensaje(m);
      const r = await req('/api/correo/ai/extraer-tareas', {
        method: 'POST', body: JSON.stringify({ message_id: m.message_id }),
      });
      const propuestas = r.tareas_propuestas || [];
      if (propuestas.length === 0) {
        let ediciones = {
          0: {
            titulo: '', descripcion: '', vencimiento: '', prioridad: 'media',
            entidad_id: m?.vinculado_entidad_id || '', contacto_id: m?.vinculado_contacto_id || '',
            entidad_nombre: '',
          },
        };
        ediciones = await enriquecerEdicionesEntidadNombre(ediciones);
        setCrearTareaRevision({
          open: true, loading: false, msg: m,
          propuestas: [{ titulo: '', descripcion: '' }],
          seleccionadas: new Set([0]),
          ediciones,
          creando: false,
          manualOnly: true,
        });
        return;
      }
      let ediciones = {};
      propuestas.forEach((p, i) => {
        ediciones[i] = {
          titulo: p.titulo || '',
          descripcion: p.descripcion || '',
          vencimiento: normalizarFechaPropuestaIA(p.fecha_propuesta),
          prioridad: p.prioridad || 'media',
          entidad_id: (p.entidad_id != null && String(p.entidad_id).trim()) ? String(p.entidad_id).trim() : (m?.vinculado_entidad_id || ''),
          contacto_id: m?.vinculado_contacto_id || '',
          entidad_nombre: '',
        };
      });
      ediciones = await enriquecerEdicionesEntidadNombre(ediciones);
      setCrearTareaRevision({
        open: true, loading: false, msg: m, propuestas,
        seleccionadas: new Set(propuestas.map((_, i) => i)),
        ediciones,
        creando: false,
        manualOnly: false,
      });
    } catch (e) {
      toast.error(`IA: ${e.message}`);
      setCrearTareaRevision({
        open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), ediciones: {}, creando: false, manualOnly: false,
      });
    }
  };

  const cerrarCrearTareaRevision = () => setCrearTareaRevision({
    open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), ediciones: {}, creando: false, manualOnly: false,
  });

  const toggleCrearTareaRevisionSel = (i) => {
    setCrearTareaRevision((t) => {
      const s = new Set(t.seleccionadas);
      if (s.has(i)) s.delete(i); else s.add(i);
      return { ...t, seleccionadas: s };
    });
  };

  const editarCrearTareaRevisionCampo = (i, campo, valor) => {
    setCrearTareaRevision((t) => {
      const prev = t.ediciones[i] || {};
      const patch = { [campo]: valor };
      if (campo === 'entidad_id') patch.entidad_nombre = '';
      return {
        ...t,
        ediciones: { ...t.ediciones, [i]: { ...prev, ...patch } },
      };
    });
  };

  const confirmarCrearTareasRevision = async () => {
    const m = crearTareaRevision.msg;
    if (!m?.message_id) return;
    setCrearTareaRevision((t) => ({ ...t, creando: true }));
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < crearTareaRevision.propuestas.length; i++) {
      if (!crearTareaRevision.seleccionadas.has(i)) continue;
      const ed = crearTareaRevision.ediciones[i] || {};
      const titulo = (ed.titulo || '').trim();
      if (titulo.length < 2) { fail++; continue; }
      const payload = {
        titulo,
        descripcion: (ed.descripcion || '').trim() || undefined,
        prioridad: ed.prioridad || 'media',
        entidad_id: (ed.entidad_id || '').trim() || undefined,
        contacto_id: (ed.contacto_id || '').trim() || undefined,
        message_id: m.message_id,
      };
      if (ed.vencimiento) payload.fecha_vencimiento = ed.vencimiento;
      try {
        await req('/api/correo/ai/crear-tarea', { method: 'POST', body: JSON.stringify(payload) });
        ok++;
      } catch (_) { fail++; }
    }
    if (ok > 0) {
      toast.success(`${ok} tarea(s) creada(s)${fail ? ` · ${fail} no creadas` : ''}`);
      setItems((prev) => prev.map((x) => x.message_id === m.message_id ? { ...x, tiene_tarea_creada: true } : x));
    } else if (fail > 0) {
      toast.error('No se creó ninguna tarea (revisa títulos de al menos 2 caracteres o permisos).');
    }
    cerrarCrearTareaRevision();
  };

  const activeMsg = activeThread?.last;

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col bg-white" data-testid="correos-gmail-page">
      {/* ─ Toolbar ─────────────────────────────────── */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-ink-200 bg-ink-50/50">
        <div className="flex items-center gap-2 flex-1">
          <Inbox className="w-4 h-4 text-brand-600" />
          <h1 className="font-heading font-semibold text-ink-900 text-base">Bandeja</h1>
          <span className="text-xs text-ink-500">· {threads.length} hilos</span>
        </div>
        <Input
          ref={searchInputRef}
          className="max-w-xs h-8 text-sm"
          placeholder="Buscar… (atajo /)"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          data-testid="gmail-busqueda"
        />
        <select
          className="h-8 text-xs border border-ink-200 rounded px-2 bg-white"
          value={filtroCuenta}
          onChange={(e) => setFiltroCuenta(e.target.value)}
          data-testid="gmail-filtro-cuenta"
        >
          <option value="all">Todas las cuentas</option>
          {cuentas.map((c) => <option key={c.id} value={c.id}>{c.email}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-ink-700">
          <input type="checkbox" checked={soloNoLeidos} onChange={(e) => setSoloNoLeidos(e.target.checked)} data-testid="gmail-filtro-no-leidos" />
          No leídos
        </label>
        <label className={`flex items-center gap-1 text-xs ${oportunidadesCount > 0 ? 'text-amber-700' : 'text-ink-400'}`}>
          <input type="checkbox" checked={soloOportunidades} onChange={(e) => setSoloOportunidades(e.target.checked)} disabled={oportunidadesCount === 0} data-testid="gmail-filtro-oportunidades" />
          🎯 {oportunidadesCount > 0 && <span className="ml-0.5">({oportunidadesCount})</span>}
        </label>
        <label className={`flex items-center gap-1 text-xs ${urgentesCount > 0 ? 'text-red-700' : 'text-ink-400'}`}>
          <input
            type="checkbox" checked={soloUrgentes}
            onChange={(e) => setSoloUrgentes(e.target.checked)}
            disabled={urgentesCount === 0}
            data-testid="gmail-filtro-urgentes"
          />
          <Flame className="w-3 h-3" /> Solo urgentes {urgentesCount > 0 && <span className="ml-0.5">({urgentesCount})</span>}
        </label>
        {sinVincularCount > 0 && (
          <button
            onClick={() => navigate('/correos/ingesta')}
            className="flex items-center gap-1.5 text-xs text-orange-700 border border-orange-300 bg-orange-50 hover:bg-orange-100 h-8 px-2 rounded transition"
            data-testid="gmail-btn-ingesta"
            title="Abrir cuadro de mando de ingesta"
          >
            Sin empresa
            <span className="bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none">
              {sinVincularCount}
            </span>
          </button>
        )}
        <Button size="sm" variant="ghost" className="h-8" onClick={() => setMostrarAtajos(true)} data-testid="gmail-btn-atajos" title="Atajos teclado (?)">
          <Keyboard className="w-4 h-4" />
        </Button>
        {ultimaSync && !sincronizando && (
          <span className="text-[10px] text-ink-400 whitespace-nowrap hidden md:block" title={new Date(ultimaSync).toLocaleString('es-ES')}>
            sync {fmtRelDate(ultimaSync)}
          </span>
        )}
        <Button size="sm" variant="outline" className="h-8" onClick={sincronizarTodas} disabled={sincronizando} data-testid="gmail-btn-sync">
          {sincronizando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Sync</>}
        </Button>
        <Button size="sm" className="h-8" onClick={() => navigate('/correos/enviados')} data-testid="gmail-btn-enviados">
          <Send className="w-3.5 h-3.5 mr-1" /> Enviados
        </Button>
        <Button size="sm" className="h-8" onClick={() => setComposer({
          open: true, to: [], cc: [], bcc: [], showCc: false, showBcc: false,
          subject: '', body: '', cuenta_id: (cuentas[0]?.id ? String(cuentas[0].id) : ''), adjuntos: [],
          in_reply_to: '', references: '', original_message_id: '',
        })} data-testid="gmail-btn-nuevo">
          <Send className="w-3.5 h-3.5 mr-1" /> Nuevo
        </Button>
      </div>

      {/* ─ Split panels ────────────────────────────── */}
      <div className="flex-1 flex min-h-0">
        {/* Lista */}
        <div className="w-[35%] min-w-[320px] border-r border-ink-200 flex flex-col" data-testid="gmail-lista-panel">
          {loading && items.length === 0 ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-brand-600 mx-auto" /></div>
          ) : threads.length === 0 ? (
            <div className="py-10 px-4 text-center text-sm text-ink-500">
              {soloOportunidades && oportunidadesCount === 0
                ? 'No hay oportunidades marcadas todavía.'
                : busquedaTerm || soloNoLeidos || filtroCuenta !== 'all'
                  ? 'No hay correos con estos filtros.'
                  : 'Sin correos sincronizados. Pulsa Sync.'}
            </div>
          ) : (
            <div className="flex-1 min-h-0">
              <List
                rowCount={threads.length}
                rowHeight={78}
                rowComponent={({ index, style }) => (
                  <ThreadRow
                    index={index}
                    style={style}
                    thread={threads[index]}
                    isActive={threads[index].key === activeThreadKey}
                    onClick={() => seleccionarThread(threads[index])}
                    onDoubleClick={() => abrirFullscreen(threads[index].last)}
                    onToggleUrgente={toggleUrgente}
                  />
                )}
                rowProps={{}}
                data-testid="gmail-virtualizada"
              />
            </div>
          )}
        </div>

        {/* Panel lectura */}
        <div className="flex-1 flex flex-col min-h-0 bg-ink-50/30" data-testid="gmail-lectura-panel">
          {!activeMsg ? (
            <div className="flex-1 flex items-center justify-center text-ink-400">
              <div className="text-center">
                <Mail className="w-12 h-12 mx-auto mb-2 text-ink-300" />
                <p className="text-sm">Selecciona un correo</p>
                <p className="text-[11px] mt-1">Atajos: <code className="bg-ink-100 px-1 rounded">j</code>/<code className="bg-ink-100 px-1 rounded">k</code> navegar · <code className="bg-ink-100 px-1 rounded">r</code> responder · <code className="bg-ink-100 px-1 rounded">/</code> buscar · <code className="bg-ink-100 px-1 rounded">?</code> ayuda</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header del correo activo */}
              <div className="px-5 py-3 border-b border-ink-200 bg-white flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-ink-900 truncate flex-1">{activeMsg.subject || '(sin asunto)'}</h2>
                    {activeMsg.es_oportunidad && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] border">🎯 Oportunidad</Badge>}
                    {activeMsg.vinculado_entidad_id ? (
                      <button
                        onClick={() => navigate(`/clientes/${activeMsg.vinculado_entidad_id}`)}
                        className="text-[10px] text-emerald-700 hover:underline flex items-center gap-0.5"
                        title="Ir a empresa vinculada"
                      >
                        <ExternalLink className="w-3 h-3" /> empresa
                      </button>
                    ) : (
                      <span className="text-[10px] text-orange-600 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5 flex items-center gap-0.5">
                        Sin empresa vinculada
                      </span>
                    )}
                    {activeMsg.vinculado_contacto_id && activeMsg.vinculado_entidad_id && (
                      <button
                        onClick={() => navigate(`/clientes/${activeMsg.vinculado_entidad_id}?tab=contactos`)}
                        className="text-[10px] text-blue-700 hover:underline flex items-center gap-0.5"
                        title={`Ver contacto en la empresa: ${activeMsg.from_name || activeMsg.from_address}`}
                      >
                        <ExternalLink className="w-3 h-3" /> contacto
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-ink-600 mt-0.5">
                    <span className="font-medium">{activeMsg.from_name || activeMsg.from_address}</span>
                    <span className="text-ink-400"> &lt;{activeMsg.from_address}&gt;</span>
                    <span className="mx-2 text-ink-300">·</span>
                    <span className="text-ink-500">{fmtRelDate(activeMsg.received_at)}</span>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setActiveThreadKey(null)} className="h-7" title="Cerrar (Esc)" data-testid="gmail-cerrar-lectura">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </div>

              {/* Toolbar acciones */}
              <div className="px-5 py-1.5 border-b border-ink-100 bg-white flex items-center gap-1 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => responder(activeMsg)} className="h-7 text-xs" data-testid="gmail-btn-responder">
                  <MessageSquareReply className="w-3.5 h-3.5 mr-1" /> Responder <span className="ml-1 text-ink-400">(r)</span>
                </Button>
                <Button size="sm" variant="outline" onClick={() => responderTodos(activeMsg)} className="h-7 text-xs" data-testid="gmail-btn-responder-todos">
                  <ReplyAll className="w-3.5 h-3.5 mr-1" /> Responder a todos
                </Button>
                <Button size="sm" variant="outline" onClick={() => reenviar(activeMsg)} className="h-7 text-xs" data-testid="gmail-btn-reenviar">
                  <Forward className="w-3.5 h-3.5 mr-1" /> Reenviar
                </Button>
                <Button size="sm" variant="outline" onClick={() => abrirCrearTareaRevision(activeMsg)} className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="gmail-btn-crear-tarea">
                  <ClipboardList className="w-3.5 h-3.5 mr-1" /> Crear tarea
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleUrgente(activeMsg)}
                  className={`h-7 text-xs ${activeMsg.urgente ? 'text-red-700 border-red-200 hover:bg-red-50' : ''}`}
                  data-testid="gmail-btn-urgente"
                >
                  <Flame className="w-3.5 h-3.5 mr-1" /> {activeMsg.urgente ? 'Quitar urgente' : 'Marcar urgente'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => abrirSugerencias(activeMsg)} className="h-7 text-xs text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50" data-testid="gmail-btn-sugerir">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Sugerir
                </Button>
                <Button size="sm" variant="outline" onClick={() => extraerTareas(activeMsg)} className="h-7 text-xs text-violet-700 border-violet-200 hover:bg-violet-50" data-testid="gmail-btn-extraer-tareas">
                  <ListChecks className="w-3.5 h-3.5 mr-1" /> Extraer tareas
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/visitas/nueva?asunto=${encodeURIComponent(activeMsg.subject || '')}&entidad_id=${activeMsg.vinculado_entidad_id || ''}`)} className="h-7 text-xs" data-testid="gmail-btn-crear-visita">
                  <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Crear visita
                </Button>
                <Button size="sm" variant="ghost" onClick={async () => {
                  try {
                    await req(`/api/correo/mensajes/${encodeURIComponent(activeMsg.message_id)}/leido?leido=false`, { method: 'PATCH' });
                    setItems((prev) => prev.map((x) => x.message_id === activeMsg.message_id ? { ...x, is_read: false } : x));
                    toast.success('Marcado como no leído');
                  } catch (e) { toast.error(e.message); }
                }} className="h-7 text-xs" data-testid="gmail-btn-marcar-no-leido">
                  Marcar no leído
                </Button>
                <Button
                  size="sm" variant="ghost"
                  className="h-7 text-xs ml-auto"
                  onClick={() => abrirFullscreen(activeMsg)}
                  data-testid="gmail-btn-fullscreen"
                  title="Ver en pantalla completa"
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Pantalla completa
                </Button>
              </div>

              {/* Panel vincular/contacto — sin empresa, o con empresa pero sin contacto */}
              {!activeMsg.ignorado && (!activeMsg.vinculado_entidad_id || !activeMsg.vinculado_contacto_id) && (
                <VincularEmpresaPanel
                  msg={activeMsg}
                  onVinculado={vincularCorreo}
                  onIgnorado={ignorarCorreo}
                  modoInicial={activeMsg.vinculado_entidad_id ? 'contacto' : 'buscar'}
                  entidadPreId={activeMsg.vinculado_entidad_id || null}
                  entidadPreNombre={activeMsg.vinculado_entidad_nombre || ''}
                />
              )}

              {/* Cuerpo */}
              <div className="flex-1 overflow-y-auto px-5 py-4 bg-white" data-testid="gmail-cuerpo">
                {activeBody.loading ? (
                  <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-brand-600 mx-auto" /></div>
                ) : activeBody.data?.body_html ? (
                  <iframe
                    title="Contenido del correo"
                    sandbox="allow-same-origin"
                    srcDoc={sanitizeHtml(activeBody.data.body_html, { cuentaId: activeMsg.cuenta_id, messageId: activeMsg.message_id })}
                    style={{ width: '100%', border: 'none', minHeight: '400px' }}
                    onLoad={(e) => {
                      const iframe = e.currentTarget;
                      iframe.style.height = iframe.contentDocument.body.scrollHeight + 'px';
                    }}
                    data-testid="gmail-body-html"
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-sm text-ink-800" data-testid="gmail-body-text">
                    {activeBody.data?.body_text || activeMsg.preview || '(sin contenido)'}
                  </pre>
                )}
              </div>

              {/* Historial del thread */}
              {activeThread.items.length > 1 && (
                <div className="border-t border-ink-200 bg-white px-5 py-2 max-h-40 overflow-y-auto" data-testid="gmail-thread-history">
                  <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-1">Hilo — {activeThread.items.length} mensajes</div>
                  <ul className="space-y-0.5">
                    {activeThread.items.slice(1).map((h, i) => (
                      <li key={h.message_id || i} className="text-[11px] text-ink-600 flex gap-2">
                        <span className="text-ink-400 shrink-0">{fmtRelDate(h.received_at)}</span>
                        <span className="truncate flex-1">{h.from_name || h.from_address} — {h.preview}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─ Dialog atajos ───────────────────── */}
      {mostrarAtajos && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMostrarAtajos(false)} data-testid="gmail-dialog-atajos">
          <Card className="max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-ink-900 mb-3 flex items-center gap-2"><Keyboard className="w-4 h-4" />Atajos de teclado (solo desktop)</h3>
            <dl className="space-y-1.5 text-sm">
              {[
                ['j / ↓', 'Siguiente correo'],
                ['k / ↑', 'Correo anterior'],
                ['r', 'Responder al correo activo'],
                ['u / Esc', 'Volver a la lista (cerrar lectura)'],
                ['/', 'Enfocar búsqueda'],
                ['?', 'Mostrar/ocultar esta ayuda'],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3">
                  <kbd className="bg-ink-100 px-2 py-0.5 rounded text-xs font-mono min-w-[72px] text-center">{k}</kbd>
                  <dd className="text-ink-700">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="flex justify-end mt-4"><Button size="sm" variant="outline" onClick={() => setMostrarAtajos(false)}>Cerrar</Button></div>
          </Card>
        </div>
      )}

      {/* ─ Composer ───────────────────── */}
      {composer.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setComposer({ ...composer, open: false }); }}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="gmail-composer">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold flex items-center gap-2"><Send className="w-5 h-5" /> Nuevo correo</h3>
              <div className="flex items-center gap-3">
                {draftSavedAt && (
                  <span className="text-[11px] text-ink-400 inline-flex items-center gap-1" data-testid="composer-draft-saved">
                    <Save className="w-3 h-3" /> Guardado {draftSavedAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                <button
                  type="button"
                  onClick={descartarBorrador}
                  className="text-[11px] text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                  data-testid="composer-descartar-borrador"
                  title="Descartar borrador y cerrar"
                >
                  <Trash2 className="w-3 h-3" /> Descartar borrador
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-ink-500">Para</label>
                  <div className="flex items-center gap-1">
                    {!composer.showCc && <button type="button" onClick={() => setComposer({ ...composer, showCc: true })} className="text-[11px] text-brand-600 hover:underline" data-testid="composer-add-cc">+ CC</button>}
                    {!composer.showBcc && <button type="button" onClick={() => setComposer({ ...composer, showBcc: true })} className="text-[11px] text-brand-600 hover:underline" data-testid="composer-add-bcc">+ CCO</button>}
                  </div>
                </div>
                <RecipientInput value={composer.to} onChange={(v) => setComposer({ ...composer, to: v })} testid="composer-to" />
              </div>
              {cuentas.length > 0 && (
                <div>
                  <label className="text-xs text-ink-500">Desde:</label>
                  <select
                    className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm bg-white"
                    value={cuentaIdEfectiva}
                    onChange={(e) => setComposer({ ...composer, cuenta_id: e.target.value })}
                    data-testid="composer-cuenta"
                  >
                    {cuentas.map((c) => (
                      <option key={c.id} value={c.id}>{c.email} ({c.tipo})</option>
                    ))}
                  </select>
                </div>
              )}
              {composer.showCc && <div><label className="text-xs text-ink-500">CC</label><RecipientInput value={composer.cc} onChange={(v) => setComposer({ ...composer, cc: v })} testid="composer-cc" /></div>}
              {composer.showBcc && <div><label className="text-xs text-ink-500">CCO</label><RecipientInput value={composer.bcc} onChange={(v) => setComposer({ ...composer, bcc: v })} testid="composer-bcc" /></div>}
              <div>
                <label className="text-xs text-ink-500">Asunto</label>
                <Input value={composer.subject} onChange={(e) => setComposer({ ...composer, subject: e.target.value })} data-testid="composer-subject" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-ink-500">Mensaje</label>
                  <ComposerAiToolbar body={composer.body} onChange={(nb) => setComposer((c) => ({ ...c, body: nb }))} />
                </div>
                <textarea rows={10} className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm" value={composer.body} onChange={(e) => setComposer({ ...composer, body: e.target.value })} data-testid="composer-body" />
              </div>
              <div>
                <label className="text-xs text-ink-500 block mb-1">Adjuntos</label>
                <AttachmentsInput value={composer.adjuntos || []} onChange={(v) => setComposer((c) => ({ ...c, adjuntos: v }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setComposer({ ...composer, open: false })}>Cancelar</Button>
              <Button onClick={enviar} disabled={composer.to.length === 0 || !composer.subject?.trim() || !composer.body?.trim() || !cuentaIdEfectiva.trim()} data-testid="composer-enviar">
                <Send className="w-4 h-4 mr-1.5" /> Enviar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ─ Dialog sugerencias IA ───────────────────── */}
      {sugerencias.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) cerrarSugerencias(); }}>
          <Card className="max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="dialog-sugerencias-gmail">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <MessageSquareReply className="w-5 h-5 text-fuchsia-600" /> Borradores de respuesta
              {sugerencias.perfilAplicado && <Badge className="bg-violet-50 text-violet-700 border-violet-200 text-[9px] border ml-1">Tu estilo</Badge>}
              {sugerencias.contexto?.vinculado && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] border ml-1">+ Contexto CRM</Badge>}
              {sugerencias.memoriasRelevantes?.length > 0 && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[9px] border ml-1">+ {sugerencias.memoriasRelevantes.length} memoria(s)</Badge>}
            </h3>
            {sugerencias.memoriasRelevantes?.length > 0 && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1.5">Memorias relevantes</div>
                <ul className="space-y-1">
                  {sugerencias.memoriasRelevantes.slice(0, 3).map((m, i) => (
                    <li key={i} className="text-[11px] text-blue-900">
                      <span className="font-medium">[{(m.received_at || '').slice(0, 10)}]</span> {m.from_address} · <em>"{m.subject || '(sin asunto)'}"</em>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {sugerencias.loading ? (
              <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-600 mx-auto" /></div>
            ) : sugerencias.borradores.length === 0 ? (
              <div className="py-6 text-center text-sm text-ink-500">Sin borradores</div>
            ) : (
              <div className="space-y-2">
                {sugerencias.borradores.map((b, i) => (
                  <div key={i} onClick={() => usarBorrador(b)} className="p-3 border border-ink-200 rounded cursor-pointer hover:border-fuchsia-300 bg-white">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 text-[10px] border">Opción {i + 1}</Badge>
                      <span className="font-medium text-ink-900 text-sm">{b.titulo}</span>
                    </div>
                    <div className="text-xs text-ink-600 mb-1"><strong>Asunto:</strong> {b.asunto}</div>
                    <pre className="text-xs text-ink-700 whitespace-pre-wrap font-sans line-clamp-6 bg-ink-50/50 rounded p-2">{b.cuerpo}</pre>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-3"><Button variant="outline" size="sm" onClick={cerrarSugerencias}>Cerrar</Button></div>
          </Card>
        </div>
      )}

      {/* ─ Dialog tareas IA ─────────────────── */}
      {tareasIA.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false, ediciones: {} }); }}>
          <Card className="max-w-3xl w-full max-h-[88vh] overflow-y-auto p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="dialog-tareas-gmail">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" /> Tareas detectadas</h3>
            {tareasIA.msg?.subject && (
              <p className="text-xs text-ink-500 mb-3">
                Origen: <em>"{tareasIA.msg.subject}"</em>
              </p>
            )}
            {tareasIA.loading ? (
              <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-violet-600 mx-auto" /></div>
            ) : tareasIA.propuestas.length === 0 ? (
              <div className="py-6 text-center text-sm text-ink-500">Sin tareas detectadas</div>
            ) : (
              <div className="space-y-3">
                {tareasIA.propuestas.map((t, i) => {
                  const sel = tareasIA.seleccionadas.has(i);
                  const ed = tareasIA.ediciones[i] || {};
                  return (
                    <div
                      key={i}
                      className={`p-3 border rounded ${sel ? 'border-violet-400 bg-violet-50/40' : 'border-ink-200 bg-white'}`}
                      data-testid={`tarea-gmail-${i}`}
                    >
                      <div className="flex items-start gap-2">
                        <button onClick={() => toggleTareaSel(i)} className="shrink-0 mt-0.5">
                          {sel ? <CheckSquare className="w-4 h-4 text-violet-600" /> : <Square className="w-4 h-4 text-ink-300" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          {/* Título editable */}
                          <input
                            type="text"
                            value={ed.titulo !== undefined ? ed.titulo : t.titulo}
                            onChange={(e) => editarTareaCampo(i, 'titulo', e.target.value)}
                            disabled={!sel}
                            className="w-full font-medium text-sm text-ink-900 border-0 border-b border-transparent focus:border-violet-300 focus:outline-none bg-transparent disabled:cursor-default mb-1"
                            data-testid={`tarea-${i}-titulo`}
                          />
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Clasificación Eisenhower — sugerida por IA, editable */}
                            <button
                              type="button"
                              disabled={!sel}
                              onClick={() => editarTareaCampo(i, 'urgente', !(ed.urgente ?? t.urgente ?? false))}
                              title={(ed.urgente ?? t.urgente) ? 'Urgente (clic para quitar)' : 'Marcar urgente'}
                              className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border transition disabled:opacity-40 ${(ed.urgente ?? t.urgente) ? 'bg-red-100 text-red-700 border-red-300' : 'bg-white text-ink-300 border-ink-200 hover:border-red-300 hover:text-red-500'}`}
                              data-testid={`tarea-${i}-urgente`}
                            >
                              <Zap className="w-3 h-3" /> Urgente
                            </button>
                            <button
                              type="button"
                              disabled={!sel}
                              onClick={() => editarTareaCampo(i, 'importante', !(ed.importante ?? t.importante ?? false))}
                              title={(ed.importante ?? t.importante) ? 'Importante (clic para quitar)' : 'Marcar importante'}
                              className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border transition disabled:opacity-40 ${(ed.importante ?? t.importante) ? 'bg-violet-100 text-violet-700 border-violet-300' : 'bg-white text-ink-300 border-ink-200 hover:border-violet-300 hover:text-violet-500'}`}
                              data-testid={`tarea-${i}-importante`}
                            >
                              <Star className="w-3 h-3" /> Importante
                            </button>
                          </div>
                          {/* Descripción editable */}
                          <textarea
                            rows={2}
                            value={ed.descripcion !== undefined ? ed.descripcion : (t.descripcion || '')}
                            onChange={(e) => editarTareaCampo(i, 'descripcion', e.target.value)}
                            disabled={!sel}
                            placeholder="Descripción (opcional)…"
                            className="w-full text-xs text-ink-600 mt-0.5 mb-2 border border-transparent hover:border-ink-200 focus:border-violet-300 focus:outline-none rounded px-1 bg-transparent resize-none disabled:cursor-default"
                            data-testid={`tarea-${i}-descripcion`}
                          />
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                            <div>
                              <label className="block text-[10px] text-ink-500 uppercase font-semibold mb-0.5">Vencimiento</label>
                              <input
                                type="date"
                                value={ed.vencimiento || ''}
                                onChange={(e) => editarTareaCampo(i, 'vencimiento', e.target.value)}
                                disabled={!sel}
                                className="w-full text-xs h-8 rounded border border-ink-200 px-2 disabled:bg-ink-50"
                                data-testid={`tarea-${i}-vencimiento`}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-500 uppercase font-semibold mb-0.5">Prioridad</label>
                              <select
                                value={ed.prioridad || 'media'}
                                onChange={(e) => editarTareaCampo(i, 'prioridad', e.target.value)}
                                disabled={!sel}
                                className="w-full text-xs h-8 rounded border border-ink-200 px-2 disabled:bg-ink-50"
                                data-testid={`tarea-${i}-prioridad`}
                              >
                                <option value="baja">Baja</option>
                                <option value="media">Media</option>
                                <option value="alta">Alta</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-500 uppercase font-semibold mb-0.5">Asignado a</label>
                              <select
                                value={ed.asignado_user_id || ''}
                                onChange={(e) => editarTareaCampo(i, 'asignado_user_id', e.target.value)}
                                disabled={!sel}
                                className="w-full text-xs h-8 rounded border border-ink-200 px-2 disabled:bg-ink-50"
                                data-testid={`tarea-${i}-asignado`}
                              >
                                {equipo.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.nombre} {u.apellidos || ''}
                                  </option>
                                ))}
                                {equipo.length === 0 && user?.id && <option value={user.id}>Yo</option>}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-500 uppercase font-semibold mb-0.5">Empresa vinculada</label>
                              <input
                                type="text"
                                value={ed.entidad_id || ''}
                                onChange={(e) => editarTareaCampo(i, 'entidad_id', e.target.value)}
                                disabled={!sel}
                                placeholder="ID entidad (opcional)"
                                className="w-full text-xs h-8 rounded border border-ink-200 px-2 disabled:bg-ink-50"
                                data-testid={`tarea-${i}-entidad`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {tareasIA.propuestas.length > 0 && (
              <div className="flex justify-between items-center mt-4">
                <span className="text-xs text-ink-500">{tareasIA.seleccionadas.size} / {tareasIA.propuestas.length}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false, ediciones: {} })}>Cancelar</Button>
                  <Button size="sm" onClick={crearTareasSeleccionadas} disabled={tareasIA.seleccionadas.size === 0 || tareasIA.creando} className="bg-violet-600 hover:bg-violet-700" data-testid="crear-tareas-gmail">
                    {tareasIA.creando ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ListChecks className="w-4 h-4 mr-1" /> Crear</>}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ─ Crear tarea desde correo (revisión IA + POST crear-tarea) ─ */}
      {crearTareaRevision.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) cerrarCrearTareaRevision(); }}>
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="dialog-crear-tarea-revision">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-emerald-600" /> Crear tarea desde correo
            </h3>
            {crearTareaRevision.msg?.subject && (
              <p className="text-xs text-ink-500 mb-2">Origen: <em>&quot;{crearTareaRevision.msg.subject}&quot;</em></p>
            )}
            {crearTareaRevision.manualOnly && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3">
                No se detectaron tareas con la IA. Completa el formulario para crear una tarea manualmente.
              </p>
            )}
            {crearTareaRevision.loading ? (
              <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-600 mx-auto" /></div>
            ) : (
              <div className="space-y-3">
                {crearTareaRevision.propuestas.map((_, i) => {
                  const sel = crearTareaRevision.seleccionadas.has(i);
                  const ed = crearTareaRevision.ediciones[i] || {};
                  return (
                    <div
                      key={i}
                      className={`p-3 border rounded ${sel ? 'border-emerald-400 bg-emerald-50/30' : 'border-ink-200 bg-white'}`}
                      data-testid={`crear-tarea-revision-row-${i}`}
                    >
                      <div className="flex items-start gap-2">
                        <button type="button" onClick={() => toggleCrearTareaRevisionSel(i)} className="shrink-0 mt-1">
                          {sel ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4 text-ink-300" />}
                        </button>
                        <div className="flex-1 min-w-0 space-y-2">
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                              <label className="text-[10px] text-ink-500 uppercase font-semibold">Título</label>
                              <ComposerAiToolbar
                                body={ed.titulo || ''}
                                onChange={(nb) => editarCrearTareaRevisionCampo(i, 'titulo', nb)}
                                testid={`crear-tarea-ia-toolbar-${i}`}
                              />
                            </div>
                            <Input
                              value={ed.titulo || ''}
                              onChange={(e) => editarCrearTareaRevisionCampo(i, 'titulo', e.target.value)}
                              disabled={!sel}
                              placeholder="Título de la tarea"
                              className="h-9 text-sm"
                              data-testid={`crear-tarea-titulo-${i}`}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Descripción</label>
                            <textarea
                              rows={3}
                              value={ed.descripcion || ''}
                              onChange={(e) => editarCrearTareaRevisionCampo(i, 'descripcion', e.target.value)}
                              disabled={!sel}
                              className="w-full text-sm rounded border border-ink-200 px-2 py-1.5 disabled:bg-ink-50"
                              data-testid={`crear-tarea-desc-${i}`}
                            />
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <div>
                              <label className="block text-[10px] text-ink-500 uppercase font-semibold mb-0.5">Vencimiento</label>
                              <input
                                type="date"
                                value={ed.vencimiento || ''}
                                onChange={(e) => editarCrearTareaRevisionCampo(i, 'vencimiento', e.target.value)}
                                disabled={!sel}
                                className="w-full text-xs h-8 rounded border border-ink-200 px-2 disabled:bg-ink-50"
                                data-testid={`crear-tarea-venc-${i}`}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-500 uppercase font-semibold mb-0.5">Prioridad</label>
                              <select
                                value={ed.prioridad || 'media'}
                                onChange={(e) => editarCrearTareaRevisionCampo(i, 'prioridad', e.target.value)}
                                disabled={!sel}
                                className="w-full text-xs h-8 rounded border border-ink-200 px-2 disabled:bg-ink-50"
                                data-testid={`crear-tarea-prio-${i}`}
                              >
                                <option value="baja">Baja</option>
                                <option value="media">Media</option>
                                <option value="alta">Alta</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-500 uppercase font-semibold mb-0.5">Empresa vinculada</label>
                              {!!(ed.entidad_nombre && ed.entidad_nombre !== ed.entidad_id) && (
                                <div className="text-[11px] text-ink-700 mb-0.5 truncate" title={ed.entidad_nombre}>{ed.entidad_nombre}</div>
                              )}
                              <input
                                type="text"
                                value={ed.entidad_id || ''}
                                onChange={(e) => editarCrearTareaRevisionCampo(i, 'entidad_id', e.target.value)}
                                onBlur={async () => {
                                  const eid = String(ed.entidad_id || '').trim();
                                  if (!eid) return;
                                  try {
                                    const ent = await req(`/api/entidades/${encodeURIComponent(eid)}`);
                                    const nombre = ent?.nombre_comercial || ent?.razon_social || '';
                                    setCrearTareaRevision((t) => ({
                                      ...t,
                                      ediciones: {
                                        ...t.ediciones,
                                        [i]: { ...(t.ediciones[i] || {}), entidad_nombre: nombre || eid },
                                      },
                                    }));
                                  } catch (_) {
                                    setCrearTareaRevision((t) => ({
                                      ...t,
                                      ediciones: {
                                        ...t.ediciones,
                                        [i]: { ...(t.ediciones[i] || {}), entidad_nombre: eid },
                                      },
                                    }));
                                  }
                                }}
                                disabled={!sel}
                                placeholder="ID entidad (opcional)"
                                className="w-full text-xs h-8 rounded border border-ink-200 px-2 disabled:bg-ink-50"
                                data-testid={`crear-tarea-entidad-${i}`}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-500 uppercase font-semibold mb-0.5">Contacto (id)</label>
                              <input
                                type="text"
                                value={ed.contacto_id || ''}
                                onChange={(e) => editarCrearTareaRevisionCampo(i, 'contacto_id', e.target.value)}
                                disabled={!sel}
                                placeholder="Opcional"
                                className="w-full text-xs h-8 rounded border border-ink-200 px-2 disabled:bg-ink-50"
                                data-testid={`crear-tarea-contacto-${i}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!crearTareaRevision.loading && (
              <div className="flex justify-between items-center mt-4 gap-2 flex-wrap">
                <span className="text-xs text-ink-500">
                  {crearTareaRevision.seleccionadas.size} seleccionada(s)
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={cerrarCrearTareaRevision}>Cancelar</Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={confirmarCrearTareasRevision}
                    disabled={
                      crearTareaRevision.creando
                      || ![...crearTareaRevision.seleccionadas].some(
                        (idx) => (crearTareaRevision.ediciones[idx]?.titulo || '').trim().length >= 2,
                      )
                    }
                    data-testid="crear-tarea-revision-confirmar"
                  >
                    {crearTareaRevision.creando ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ListChecks className="w-4 h-4 mr-1" /> Crear tareas seleccionadas</>}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Iter68 — Modal fullscreen al hacer doble clic en una fila */}
      {fullscreenMsg && (
        <EmailViewer
          mensaje={fullscreenMsg}
          fullscreen
          onClose={cerrarFullscreen}
          onResponder={(m, b) => { responder(m, b); cerrarFullscreen(); }}
          onResponderTodos={(m, b) => { responderTodos(m, b); cerrarFullscreen(); }}
          onReenviar={(m, b) => { reenviar(m, b); cerrarFullscreen(); }}
          onExtraerTareas={(m) => { extraerTareas(m); cerrarFullscreen(); }}
          onSugerir={(m) => { abrirSugerencias(m); cerrarFullscreen(); }}
          onCrearVisita={(m) => navigate(`/visitas/nueva?asunto=${encodeURIComponent(m.subject || '')}&entidad_id=${m.vinculado_entidad_id || ''}`)}
          onToggleUrgente={toggleUrgente}
        />
      )}
    </div>
  );
}
